import { useModelLabel } from "@judo/i18n";
import type { EnumerationCombo, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { Autocomplete, TextField } from "@mui/material";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface EnumerationComboOption {
	value: string;
	label: string;
}

export interface EnumerationComboComponentProps {
	element: EnumerationCombo | Input;
	readOnly?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
	options?: EnumerationComboOption[];
	/** When true, allows empty selection (clearable). @default false */
	nullable?: boolean;
	judoClassName?: string;
}

/**
 * Enumeration combo component using MUI Autocomplete.
 * Provides type-to-search filtering matching the template's Autocomplete pattern.
 */
export function EnumerationComboComponent({
	element,
	readOnly,
	value = "",
	onChange,
	error,
	disabled,
	options = [],
	nullable = false,
	judoClassName,
}: EnumerationComboComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const startAdornment = buildIconAdornment(element.icon);

	// Find the selected option object from the value string
	const selectedOption = options.find((opt) => opt.value === value) ?? null;

	return (
		<Autocomplete
			autoHighlight
			options={options}
			value={selectedOption}
			onChange={(_event, newValue) => {
				onChange?.(newValue?.value ?? "");
			}}
			getOptionLabel={(option) => option.label}
			isOptionEqualToValue={(option, val) => option.value === val.value}
			disableClearable={!nullable}
			disabled={disabled ?? element.disabled}
			readOnly={readOnly ?? element.isReadOnly}
			fullWidth
			data-testid={testId}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={element.required}
					error={!!error}
					helperText={error}
					className={judoClassName}
					slotProps={{
						input: {
							...params.InputProps,
							startAdornment: startAdornment ? (
								<>
									{startAdornment}
									{params.InputProps.startAdornment}
								</>
							) : (
								params.InputProps.startAdornment
							),
						},
						inputLabel: {
							shrink: true,
						},
					}}
				/>
			)}
		/>
	);
}
